import React, { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Eye, Users, TrendingUp, Globe, Monitor, Smartphone, MapPin, Clock, MessageCircle, FileDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const day = (d) => d.toISOString().slice(0, 10);
const daysAgo = (n) => day(new Date(Date.now() - n * 86400000));
const PRESETS = [
  { key: "today", label: "Today", start: () => daysAgo(0) },
  { key: "7d", label: "7 Days", start: () => daysAgo(6) },
  { key: "30d", label: "30 Days", start: () => daysAgo(29) },
  { key: "90d", label: "90 Days", start: () => daysAgo(89) },
  { key: "year", label: "1 Year", start: () => daysAgo(364) },
  { key: "all", label: "All Time", start: () => "" },
];

const StatCard = ({ icon: Icon, label, value, testId }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid={testId}>
    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.35em] text-zinc-400">
      <Icon size={13} className="text-purple-400" /> {label}
    </div>
    <div className="font-display text-4xl text-zinc-100 mt-3">{value.toLocaleString()}</div>
  </div>
);

const AnalyticsDashboard = ({ adminKey }) => {
  const [data, setData] = useState(null);
  const [sessions, setSessions] = useState(null);
  const [err, setErr] = useState("");
  const [preset, setPreset] = useState("30d");
  const [range, setRange] = useState({ start: daysAgo(29), end: "" });
  const [custom, setCustom] = useState({ start: "", end: "" });

  const applyPreset = (p) => {
    setPreset(p.key);
    setRange({ start: p.start(), end: "" });
  };

  const applyCustom = () => {
    if (!custom.start && !custom.end) return;
    setPreset("custom");
    setRange({ start: custom.start, end: custom.end });
  };

  useEffect(() => {
    let alive = true;
    setData(null);
    const params = {};
    if (range.start) params.start = range.start;
    if (range.end) params.end = range.end;
    axios
      .get(`${API}/analytics/summary`, { params })
      .then(({ data }) => alive && setData(data))
      .catch(() => alive && setErr("Failed to load analytics."));
    axios
      .get(`${API}/analytics/sessions`, { params })
      .then(({ data }) => alive && setSessions(data.sessions || []))
      .catch(() => alive && setSessions([]));
    return () => { alive = false; };
  }, [adminKey, range]);

  if (err) return <div className="text-red-400 text-sm" data-testid="analytics-error">{err}</div>;

  const rangePicker = (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 flex flex-wrap items-center gap-2" data-testid="analytics-range-picker">
      {PRESETS.map((p) => (
        <button
          key={p.key}
          onClick={() => applyPreset(p)}
          data-testid={`range-preset-${p.key}`}
          className={`rounded-full px-4 py-1.5 text-[11px] uppercase tracking-[0.15em] transition-colors ${
            preset === p.key
              ? "bg-gradient-to-r from-[#E5C56F] to-[#C9A64B] text-[#1a1405] font-semibold"
              : "border border-white/15 text-zinc-400 hover:text-zinc-100 hover:border-white/30"
          }`}
        >
          {p.label}
        </button>
      ))}
      <span className="hidden sm:block w-px h-6 bg-white/10 mx-1" />
      <input
        type="date"
        value={custom.start}
        onChange={(e) => setCustom((c) => ({ ...c, start: e.target.value }))}
        data-testid="range-custom-start"
        className="rounded-lg bg-white/[0.04] border border-white/15 px-3 py-1.5 text-[12px] text-zinc-200 [color-scheme:dark]"
      />
      <span className="text-zinc-500 text-xs">→</span>
      <input
        type="date"
        value={custom.end}
        onChange={(e) => setCustom((c) => ({ ...c, end: e.target.value }))}
        data-testid="range-custom-end"
        className="rounded-lg bg-white/[0.04] border border-white/15 px-3 py-1.5 text-[12px] text-zinc-200 [color-scheme:dark]"
      />
      <button
        onClick={applyCustom}
        data-testid="range-custom-apply"
        className={`rounded-full px-4 py-1.5 text-[11px] uppercase tracking-[0.15em] transition-colors ${
          preset === "custom"
            ? "bg-gradient-to-r from-[#E5C56F] to-[#C9A64B] text-[#1a1405] font-semibold"
            : "border border-purple-500/40 text-purple-200 hover:bg-purple-600/15"
        }`}
      >
        Apply
      </button>
    </div>
  );

  if (!data)
    return (
      <div className="space-y-8">
        {rangePicker}
        <div className="flex items-center gap-3 text-zinc-400" data-testid="analytics-loading">
          <Loader2 className="animate-spin" size={16} /> Loading analytics…
        </div>
      </div>
    );

  const maxDaily = Math.max(1, ...data.daily.map((d) => d.views));
  const totalDevice = data.devices.reduce((s, d) => s + d.views, 0) || 1;

  return (
    <div className="space-y-8" data-testid="analytics-dashboard">
      {rangePicker}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Eye} label="Views (range)" value={data.total_views} testId="stat-total-views" />
        <StatCard icon={Users} label="Visitors (range)" value={data.unique_visitors} testId="stat-unique-visitors" />
        <StatCard icon={TrendingUp} label="Views Today" value={data.views_today} testId="stat-views-today" />
        <StatCard icon={Users} label="Visitors Today" value={data.visitors_today} testId="stat-visitors-today" />
      </div>

      {data.conversions && (
        <div className="rounded-2xl border border-[#E5C56F]/20 bg-[#E5C56F]/[0.03] p-6" data-testid="analytics-conversions">
          <div className="text-[10px] uppercase tracking-[0.35em] text-[#E5C56F] mb-5">Conversions</div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div data-testid="stat-whatsapp-clicks">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-400">
                <MessageCircle size={12} className="text-emerald-400" /> WhatsApp Clicks
              </div>
              <div className="font-display text-3xl text-zinc-100 mt-2">{data.conversions.whatsapp_clicks.toLocaleString()}</div>
              <div className="text-[11px] text-zinc-500 mt-1">{data.conversions.whatsapp_today} today</div>
            </div>
            <div data-testid="stat-signups">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-400">
                <FileDown size={12} className="text-[#E5C56F]" /> Checklist Signups
              </div>
              <div className="font-display text-3xl text-zinc-100 mt-2">{data.conversions.signups.toLocaleString()}</div>
              <div className="text-[11px] text-zinc-500 mt-1">{data.conversions.signups_today} today</div>
            </div>
            <div data-testid="stat-leads-total">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-400">
                <Users size={12} className="text-purple-400" /> Total Leads (emails)
              </div>
              <div className="font-display text-3xl text-zinc-100 mt-2">{data.conversions.leads_total.toLocaleString()}</div>
            </div>
            <div data-testid="stat-conversion-rate">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-400">
                <TrendingUp size={12} className="text-emerald-400" /> WhatsApp CTR
              </div>
              <div className="font-display text-3xl text-zinc-100 mt-2">
                {data.unique_visitors ? `${((data.conversions.whatsapp_clicks / data.unique_visitors) * 100).toFixed(1)}%` : "—"}
              </div>
              <div className="text-[11px] text-zinc-500 mt-1">clicks / unique visitors</div>
            </div>
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid="analytics-daily-chart">
        <div className="text-[10px] uppercase tracking-[0.35em] text-zinc-400 mb-6">
          {preset === "all" ? "Last 30 days · Views" : "Daily views · selected range"}
        </div>
        {data.daily.length === 0 ? (
          <div className="text-zinc-500 text-sm">No traffic recorded yet.</div>
        ) : (
          <div className="flex items-end gap-[3px] h-36">
            {data.daily.map((d) => (
              <div key={d.day} className="flex-1 group relative flex flex-col justify-end h-full">
                <div
                  className="w-full rounded-t bg-gradient-to-t from-purple-800 to-purple-500 group-hover:from-purple-600 group-hover:to-[#E5C56F] transition-colors"
                  style={{ height: `${Math.max(4, (d.views / maxDaily) * 100)}%` }}
                />
                <div className="absolute -top-9 left-1/2 -translate-x-1/2 hidden group-hover:block bg-black border border-white/20 rounded px-2 py-1 text-[10px] text-zinc-200 whitespace-nowrap z-10">
                  {d.day} · {d.views} views · {d.visitors} visitors
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid="analytics-top-pages">
          <div className="text-[10px] uppercase tracking-[0.35em] text-zinc-400 mb-5">Top Pages</div>
          {data.top_pages.length === 0 && <div className="text-zinc-500 text-sm">No data yet.</div>}
          <div className="space-y-3">
            {data.top_pages.map((p) => (
              <div key={p.path} className="flex items-center justify-between text-sm">
                <span className="text-zinc-300 truncate mr-3">{p.path}</span>
                <span className="text-purple-300 font-medium shrink-0">{p.views}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid="analytics-referrers">
          <div className="text-[10px] uppercase tracking-[0.35em] text-zinc-400 mb-5 flex items-center gap-2">
            <Globe size={12} /> Top Referrers
          </div>
          {data.top_referrers.length === 0 && <div className="text-zinc-500 text-sm">Direct traffic only so far.</div>}
          <div className="space-y-3">
            {data.top_referrers.map((r) => (
              <div key={r.referrer} className="flex items-center justify-between text-sm">
                <span className="text-zinc-300 truncate mr-3">{r.referrer}</span>
                <span className="text-purple-300 font-medium shrink-0">{r.views}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid="analytics-devices">
          <div className="text-[10px] uppercase tracking-[0.35em] text-zinc-400 mb-5">Devices</div>
          {data.devices.length === 0 && <div className="text-zinc-500 text-sm">No data yet.</div>}
          <div className="space-y-4">
            {data.devices.map((d) => (
              <div key={d.device}>
                <div className="flex items-center justify-between text-sm mb-1.5">
                  <span className="text-zinc-300 flex items-center gap-2 capitalize">
                    {d.device === "mobile" ? <Smartphone size={13} /> : <Monitor size={13} />} {d.device}
                  </span>
                  <span className="text-purple-300 font-medium">{Math.round((d.views / totalDevice) * 100)}%</span>
                </div>
                <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-purple-600 to-[#E5C56F]"
                    style={{ width: `${(d.views / totalDevice) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid="analytics-countries">
        <div className="text-[10px] uppercase tracking-[0.35em] text-zinc-400 mb-5 flex items-center gap-2">
          <MapPin size={12} /> Visitors by Country
        </div>
        {(!data.countries || data.countries.length === 0) && (
          <div className="text-zinc-500 text-sm">No geo data yet — collected from new visits going forward.</div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3">
          {(data.countries || []).map((c) => (
            <div key={c.country} className="flex items-center justify-between text-sm">
              <span className="text-zinc-300 truncate mr-3">{c.country}</span>
              <span className="shrink-0">
                <span className="text-purple-300 font-medium">{c.visitors}</span>
                <span className="text-zinc-500 text-xs ml-2">{c.views} views</span>
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid="analytics-sessions">
        <div className="text-[10px] uppercase tracking-[0.35em] text-zinc-400 mb-5 flex items-center gap-2">
          <Clock size={12} /> Recent Visits · who, from where, how long, where they stopped
        </div>
        {sessions === null && (
          <div className="flex items-center gap-3 text-zinc-400 text-sm"><Loader2 className="animate-spin" size={14} /> Loading…</div>
        )}
        {sessions !== null && sessions.length === 0 && (
          <div className="text-zinc-500 text-sm">No sessions recorded yet.</div>
        )}
        {sessions !== null && sessions.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-[13px]">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 border-b border-white/10">
                  <th className="py-2.5 pr-4">When</th>
                  <th className="py-2.5 pr-4">Location</th>
                  <th className="py-2.5 pr-4">Device</th>
                  <th className="py-2.5 pr-4">Came from</th>
                  <th className="py-2.5 pr-4">Entry page</th>
                  <th className="py-2.5 pr-4">Stopped at</th>
                  <th className="py-2.5 pr-4">Pages</th>
                  <th className="py-2.5">Stayed</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => (
                  <tr key={s.session_id} className="border-b border-white/[0.05] text-zinc-300" data-testid={`session-row-${s.session_id}`}>
                    <td className="py-2.5 pr-4 whitespace-nowrap text-zinc-400">{fmtWhen(s.started_at)}</td>
                    <td className="py-2.5 pr-4 whitespace-nowrap">
                      {s.country ? (
                        <span>{s.city ? `${s.city}, ` : ""}<span className="text-zinc-100">{s.country}</span></span>
                      ) : (
                        <span className="text-zinc-600">Unknown</span>
                      )}
                    </td>
                    <td className="py-2.5 pr-4 capitalize">{s.device}</td>
                    <td className="py-2.5 pr-4 max-w-[140px] truncate text-zinc-400">{s.referrer || "Direct"}</td>
                    <td className="py-2.5 pr-4 text-purple-300">{s.entry || "—"}</td>
                    <td className="py-2.5 pr-4 text-[#E5C56F]">{s.exit || "—"}</td>
                    <td className="py-2.5 pr-4">{s.page_count}</td>
                    <td className="py-2.5 whitespace-nowrap">{fmtDur(s.duration_sec)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const fmtDur = (sec) => {
  if (!sec || sec < 1) return "<5s";
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const fmtWhen = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch (e) {
    return iso;
  }
};

export default AnalyticsDashboard;
